import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { TicketCheckIcon, CalendarIcon, PrinterIcon, SearchIcon, BuildingIcon } from 'lucide-react';
interface CouponUsedRecord {
  id: string;
  couponCode: string;
  vendor: string;
  amount: number;
  usedDate: string;
}
const mockData: CouponUsedRecord[] = [{
  id: '1',
  couponCode: 'COUP-3102',
  vendor: 'Metro Cargo Services',
  amount: 250,
  usedDate: '2026-04-03'
}, {
  id: '2',
  couponCode: 'COUP-6745',
  vendor: 'National Freight Lines',
  amount: 750,
  usedDate: '2026-04-05'
}, {
  id: '3',
  couponCode: 'COUP-2218',
  vendor: 'Logistics Express Pvt Ltd',
  amount: 500,
  usedDate: '2026-04-06'
}, {
  id: '4',
  couponCode: 'COUP-8891',
  vendor: 'FastTrack Transport Co.',
  amount: 350,
  usedDate: '2026-04-08'
}, {
  id: '5',
  couponCode: 'COUP-5540',
  vendor: 'BlueDart Logistics',
  amount: 1000,
  usedDate: '2026-04-09'
}, {
  id: '6',
  couponCode: 'COUP-1123',
  vendor: 'SafeMove Carriers',
  amount: 200,
  usedDate: '2026-04-10'
}, {
  id: '7',
  couponCode: 'COUP-7766',
  vendor: 'Metro Cargo Services',
  amount: 450,
  usedDate: '2026-04-11'
}, {
  id: '8',
  couponCode: 'COUP-4432',
  vendor: 'National Freight Lines',
  amount: 600,
  usedDate: '2026-04-12'
}, {
  id: '9',
  couponCode: 'COUP-9901',
  vendor: 'Logistics Express Pvt Ltd',
  amount: 300,
  usedDate: '2026-04-13'
}, {
  id: '10',
  couponCode: 'COUP-3355',
  vendor: 'FastTrack Transport Co.',
  amount: 800,
  usedDate: '2026-04-14'
}];
interface VendorSummary {
  vendor: string;
  count: number;
  totalAmount: number;
}
export function POSCouponUsedReportPage() {
  const [startDate, setStartDate] = useState('2026-04-01');
  const [endDate, setEndDate] = useState('2026-04-15');
  const [selectedVendor, setSelectedVendor] = useState('');
  const [hasGenerated, setHasGenerated] = useState(false);
  const vendors = useMemo(() => [...new Set(mockData.map(r => r.vendor))].sort(), []);
  const filteredData = useMemo(() => {
    if (!selectedVendor) return mockData;
    return mockData.filter(r => r.vendor === selectedVendor);
  }, [selectedVendor]);
  const vendorSummaries = useMemo((): VendorSummary[] => {
    const map = new Map<string, {
      count: number;
      totalAmount: number;
    }>();
    filteredData.forEach(r => {
      const existing = map.get(r.vendor) || {
        count: 0,
        totalAmount: 0
      };
      map.set(r.vendor, {
        count: existing.count + 1,
        totalAmount: existing.totalAmount + r.amount
      });
    });
    return Array.from(map.entries()).map(([vendor, data]) => ({
      vendor,
      ...data
    })).sort((a, b) => b.totalAmount - a.totalAmount);
  }, [filteredData]);
  const totalCoupons = filteredData.length;
  const totalAmount = filteredData.reduce((s, r) => s + r.amount, 0);
  const handleGenerate = () => {
    setHasGenerated(true);
  };
  const handlePrint = () => {
    document.body.classList.add('print-thermal');
    window.print();
    document.body.classList.remove('print-thermal');
  };
  return <div className="w-full max-w-4xl mx-auto" data-id="element-973">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100 p-5 xs:p-6 sm:p-8 no-print" data-id="element-974">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6" data-id="element-975">
          <div className="flex items-center gap-3" data-id="element-976">
            <div className="bg-brand-teal/10 p-2 rounded-xl border border-brand-teal/20" data-id="element-977">
              <TicketCheckIcon className="w-6 h-6 text-brand-teal" data-id="element-978" />
            </div>
            <div data-id="element-979">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight" data-id="element-980">
                POS Coupon Used Report
              </h2>
              <p className="text-sm text-slate-500" data-id="element-981">
                Vendor-wise coupon summary for thermal print (80mm).
              </p>
            </div>
          </div>
          {hasGenerated && <button onClick={handlePrint} className="inline-flex items-center justify-center gap-2 bg-brand-teal text-white px-4 py-2.5 rounded-xl font-medium text-sm hover:bg-brand-tealHover transition-colors" data-id="element-982">
              <PrinterIcon className="w-4 h-4" data-id="element-983" />
              Print (80mm)
            </button>}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8 p-4 bg-slate-50 rounded-xl border border-slate-200" data-id="element-984">
          <div className="flex-1" data-id="element-985">
            <label className="block text-xs font-semibold text-slate-500 mb-1.5" data-id="element-986">
              Vendor
            </label>
            <div className="relative" data-id="element-987">
              <BuildingIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-slate-400 pointer-events-none" data-id="element-988" />
              <select value={selectedVendor} onChange={e => {
              setSelectedVendor(e.target.value);
              setHasGenerated(false);
            }} className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-teal/30 focus:border-brand-teal transition-all appearance-none" data-id="element-989">
                <option value="" data-id="element-990">All Vendors</option>
                {vendors.map(v => <option key={v} value={v} data-id="element-991">
                    {v}
                  </option>)}
              </select>
            </div>
          </div>
          <div className="flex-1" data-id="element-992">
            <label className="block text-xs font-semibold text-slate-500 mb-1.5" data-id="element-993">
              Start Date
            </label>
            <div className="relative" data-id="element-994">
              <CalendarIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-slate-400 pointer-events-none" data-id="element-995" />
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-teal/30 focus:border-brand-teal transition-all" data-id="element-996" />
            </div>
          </div>
          <div className="flex-1" data-id="element-997">
            <label className="block text-xs font-semibold text-slate-500 mb-1.5" data-id="element-998">
              End Date
            </label>
            <div className="relative" data-id="element-999">
              <CalendarIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-slate-400 pointer-events-none" data-id="element-1000" />
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-teal/30 focus:border-brand-teal transition-all" data-id="element-1001" />
            </div>
          </div>
          <div className="flex items-end" data-id="element-1002">
            <button onClick={handleGenerate} className="w-full sm:w-auto px-6 py-2.5 bg-slate-800 text-white rounded-xl font-medium text-sm hover:bg-slate-700 transition-colors flex items-center justify-center gap-2 h-[42px]" data-id="element-1003">
              <SearchIcon className="w-4 h-4" data-id="element-1004" />
              Generate
            </button>
          </div>
        </div>

        {/* Screen Summary */}
        {hasGenerated && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} data-id="element-1005">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 gap-3 mb-5" data-id="element-1006">
              <div className="bg-orange-50 rounded-xl p-4 border border-orange-100 text-center" data-id="element-1007">
                <p className="text-xs font-semibold text-orange-600 mb-1" data-id="element-1008">
                  Total Coupons Used
                </p>
                <p className="text-3xl font-bold text-orange-800" data-id="element-1009">
                  {totalCoupons}
                </p>
              </div>
              <div className="bg-brand-teal/5 rounded-xl p-4 border border-brand-teal/20 text-center" data-id="element-1010">
                <p className="text-xs font-semibold text-brand-teal mb-1" data-id="element-1011">
                  Total Amount
                </p>
                <p className="text-3xl font-bold text-brand-teal" data-id="element-1012">
                  ₹{totalAmount.toLocaleString()}
                </p>
              </div>
            </div>

            {/* Vendor Summary Table */}
            <div className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-1013">
              <div className="bg-slate-50 px-4 py-3 border-b border-slate-200" data-id="element-1014">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-1015">
                  Vendor-wise Summary
                </p>
              </div>
              <table className="w-full text-sm" data-id="element-1016">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-500" data-id="element-1017">
                  <tr data-id="element-1018">
                    <th className="px-4 py-2.5 text-left font-semibold" data-id="element-1019">
                      Vendor
                    </th>
                    <th className="px-4 py-2.5 text-center font-semibold" data-id="element-1020">
                      Coupons
                    </th>
                    <th className="px-4 py-2.5 text-right font-semibold" data-id="element-1021">
                      Amount (₹)
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-id="element-1022">
                  {vendorSummaries.map(vs => <tr key={vs.vendor} className="hover:bg-slate-50/50 transition-colors" data-id="element-1023">
                      <td className="px-4 py-3 text-slate-800 font-medium text-xs" data-id="element-1024">
                        {vs.vendor}
                      </td>
                      <td className="px-4 py-3 text-center" data-id="element-1025">
                        <span className="inline-flex items-center justify-center min-w-[28px] h-6 px-2 rounded-md bg-orange-50 text-orange-700 text-xs font-semibold" data-id="element-1026">
                          {vs.count}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-slate-900" data-id="element-1027">
                        ₹{vs.totalAmount.toLocaleString()}
                      </td>
                    </tr>)}
                </tbody>
                <tfoot className="bg-slate-50 border-t border-slate-200 font-bold" data-id="element-1028">
                  <tr data-id="element-1029">
                    <td className="px-4 py-3 text-slate-900" data-id="element-1030">Total</td>
                    <td className="px-4 py-3 text-center text-brand-teal" data-id="element-1031">
                      {totalCoupons}
                    </td>
                    <td className="px-4 py-3 text-right text-brand-teal" data-id="element-1032">
                      ₹{totalAmount.toLocaleString()}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </motion.div>}

        {!hasGenerated && <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center bg-slate-50" data-id="element-1033">
            <TicketCheckIcon className="w-10 h-10 text-slate-300 mx-auto mb-3" data-id="element-1034" />
            <p className="text-slate-500 text-sm font-medium" data-id="element-1035">
              Select filters and generate to view summary
            </p>
          </div>}
      </motion.div>

      {/* 80mm Thermal Print Layout */}
      {hasGenerated && <div id="print-receipt" className="hidden print:block bg-white font-mono text-[11px] leading-[1.4] text-black p-4 w-[302px] print:w-[80mm] print:p-[4mm]" data-id="element-1036">
          <div className="text-center mb-1" data-id="element-1037">
            <div className="text-[15px] font-bold tracking-wide" data-id="element-1038">
              ERPLogiPark
            </div>
            <div className="text-[9px] leading-tight mt-0.5 text-gray-700" data-id="element-1039">
              Terminal Management &amp; Gate Automation
            </div>
          </div>
          <div className="text-center text-[9px] leading-tight text-gray-600 mb-2" data-id="element-1040">
            Plot No. 45, Logistics Hub, Sector 18
            <br data-id="element-1041" />
            Industrial Area, Bangalore - 560100
          </div>

          <div className="border-t border-dashed border-gray-400 my-2" data-id="element-1042" />

          <div className="text-center font-bold text-[12px] tracking-wider mb-1" data-id="element-1043">
            COUPON USED SUMMARY
          </div>
          {selectedVendor && <div className="text-center text-[10px] font-bold mb-0.5" data-id="element-1044">
              {selectedVendor}
            </div>}
          <div className="text-center text-[9px] text-gray-600 mb-1" data-id="element-1045">
            {startDate} to {endDate}
          </div>

          <div className="border-t border-dashed border-gray-400 my-2" data-id="element-1046" />

          {/* Vendor-wise breakdown */}
          <div className="space-y-1.5" data-id="element-1047">
            {vendorSummaries.map((vs, idx) => <div key={vs.vendor} data-id="element-1048">
                <div className="flex justify-between text-[10px]" data-id="element-1049">
                  <span className="font-bold truncate max-w-[180px]" data-id="element-1050">
                    {vs.vendor}
                  </span>
                  <span data-id="element-1051">x{vs.count}</span>
                </div>
                <div className="flex justify-between text-[11px]" data-id="element-1052">
                  <span className="text-gray-500" data-id="element-1053">Amount:</span>
                  <span className="font-bold" data-id="element-1054">
                    ₹{vs.totalAmount.toLocaleString()}
                  </span>
                </div>
                {idx < vendorSummaries.length - 1 && <div className="border-t border-dotted border-gray-300 mt-1" data-id="element-1055" />}
              </div>)}
          </div>

          <div className="border-t border-dashed border-gray-400 my-2" data-id="element-1056" />

          {/* Totals */}
          <div className="space-y-[4px]" data-id="element-1057">
            <div className="flex justify-between text-[11px]" data-id="element-1058">
              <span data-id="element-1059">Total Coupons:</span>
              <span className="font-bold" data-id="element-1060">{totalCoupons}</span>
            </div>
            <div className="border-t border-dotted border-gray-300 my-1" data-id="element-1061" />
            <div className="flex justify-between font-bold text-[13px]" data-id="element-1062">
              <span data-id="element-1063">GRAND TOTAL:</span>
              <span data-id="element-1064">₹{totalAmount.toLocaleString()}</span>
            </div>
          </div>

          <div className="border-t border-dashed border-gray-400 my-3" data-id="element-1065" />

          <div className="text-center text-[8px] text-gray-500 leading-tight" data-id="element-1066">
            <div data-id="element-1067">Generated: {new Date().toLocaleString()}</div>
            <div data-id="element-1068">This is a computer generated report.</div>
            <div className="mt-1" data-id="element-1069">© 2026 ERPLogiPark</div>
          </div>
        </div>}
    </div>;
}